import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchWillingData,
  fetchWillingConversations,
} from "../../../services/insightModule/insightService";
import DatePickerComponent from "../../../common/components/DatePickerComponent";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const PURPLE = "#7F77DD";
const TEAL   = "#85B7EB";

const WILLING_COLORS = {
  "Full today":     GREEN,
  "Partial today":  TEAL,
  "Full future":    PURPLE,
  "Partial future": AMBER,
  "Not Willing":    PINK,
};

const WILLING_KEYS = [
  "Full today",
  "Partial today",
  "Full future",
  "Partial future",
  "Not Willing",
];

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: "#f7f7f7", borderRadius: 2,
        p: "10px 14px", minWidth: 0, flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": onClick ? { background: "#efefef" } : {},
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Willing = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [page,      setPage]      = useState(0);
  const [drill,     setDrill]     = useState(null);
  const rowsPerPage = 25;

  const load = useCallback(async (start, end, ch) => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const res = await fetchWillingData({
        startDate:   start ? start.toISOString() : null,
        endDate:     end   ? end.toISOString()   : null,
        channeltype: ch,
      });
      setData(Array.isArray(res) ? res : []);
    } catch (e) {
      console.error(e);
      setError("Failed to load willingness data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(startDate, endDate, "Texts");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KPIs ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total      = data.reduce((a,b) => a + safeNum(b["Total"]),           0);
    const fullToday  = data.reduce((a,b) => a + safeNum(b["Full today"]),      0);
    const partToday  = data.reduce((a,b) => a + safeNum(b["Partial today"]),   0);
    const fullFut    = data.reduce((a,b) => a + safeNum(b["Full future"]),     0);
    const partFut    = data.reduce((a,b) => a + safeNum(b["Partial future"]),  0);
    const notWilling = data.reduce((a,b) => a + safeNum(b["Not Willing"]),     0);
    return {
      total, fullToday, partToday, fullFut, partFut, notWilling,
      fullTodayPct:  total > 0 ? fullToday  / total * 100 : 0,
      partTodayPct:  total > 0 ? partToday  / total * 100 : 0,
      fullFutPct:    total > 0 ? fullFut    / total * 100 : 0,
      partFutPct:    total > 0 ? partFut    / total * 100 : 0,
      notWillingPct: total > 0 ? notWilling / total * 100 : 0,
      willingPct:    total > 0 ? (fullToday + partToday + fullFut + partFut) / total * 100 : 0,
    };
  }, [data]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    [...data]
      .sort((a, b) => {
        const da = parseLocalDate(a.ConvDate);
        const db = parseLocalDate(b.ConvDate);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      })
      .map(r => ({
        date:             fmtDate(r.ConvDate),
        _convDate:        r.ConvDate,
        "Full today":     safeNum(r["Full today"]),
        "Partial today":  safeNum(r["Partial today"]),
        "Full future":    safeNum(r["Full future"]),
        "Partial future": safeNum(r["Partial future"]),
        "Not Willing":    safeNum(r["Not Willing"]),
      }))
  , [data]);

  // ── Distribution bar ───────────────────────────────────────────
  const distData = useMemo(() => WILLING_KEYS.map(key => ({
    label: key,
    key,
    count: data.reduce((a,b) => a + safeNum(b[key]), 0),
    pct:   kpis.total > 0
      ? data.reduce((a,b) => a + safeNum(b[key]), 0) / kpis.total * 100
      : 0,
    color: WILLING_COLORS[key],
  })), [data, kpis.total]);

  // ── Pagination ─────────────────────────────────────────────────
  const sortedData = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a.ConvDate);
      const db = parseLocalDate(b.ConvDate);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data]);

  const pagedData  = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ── Drill-down ─────────────────────────────────────────────────
  const openDrill = useCallback(({ willing = null, date = null }) => {
    const willingColor = willing ? WILLING_COLORS[willing] : null;

    const title =
      willing && date ? "Conversations by willingness & date" :
      willing         ? `Conversations: ${willing}` :
                        `Conversations on ${fmtDate(date)}`;

    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: willing ? { label: "Willingness", value: willing, color: willingColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      // The DB description likely contains the bucket phrase verbatim,
      // so the chip on each card lights up in the matching color.
      emotionColors: WILLING_COLORS,
      fetchData: () => fetchWillingConversations({
        willing,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [startDate, endDate, channel]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // 40px drill icon column added at the start
  const COLS    = "40px 100px 100px 110px 110px 110px 110px 80px";
  const HEADERS = ["", "Date", "Not Willing", "Full Today", "Partial Today", "Full Future", "Partial Future", "Total"];

  // Cell key + display color, in the order the columns appear after Date
  const CELL_COLS = [
    { key: "Not Willing",    color: PINK   },
    { key: "Full today",     color: GREEN  },
    { key: "Partial today",  color: TEAL   },
    { key: "Full future",    color: PURPLE },
    { key: "Partial future", color: AMBER  },
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d && (!startDate || d >= startDate)) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, channel)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setChannel(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          {/* Top row — totals. "Willing %" is a combined bucket so non-clickable. */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard label="Total records"    value={kpis.total.toLocaleString()}         color={BLUE}  />
            <KpiCard label="Willing %"        value={`${kpis.willingPct.toFixed(1)}%`}    color={GREEN} />
            <KpiCard label="Not willing %"    value={`${kpis.notWillingPct.toFixed(1)}%`} color={PINK}
              onClick={() => openDrill({ willing: "Not Willing" })} />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="Full today %"     value={`${kpis.fullTodayPct.toFixed(1)}%`}  color={GREEN}
              onClick={() => openDrill({ willing: "Full today" })} />
            <KpiCard label="Partial today %"  value={`${kpis.partTodayPct.toFixed(1)}%`}  color={TEAL}
              onClick={() => openDrill({ willing: "Partial today" })} />
            <KpiCard label="Full future %"    value={`${kpis.fullFutPct.toFixed(1)}%`}    color={PURPLE}
              onClick={() => openDrill({ willing: "Full future" })} />
            <KpiCard label="Partial future %" value={`${kpis.partFutPct.toFixed(1)}%`}    color={AMBER}
              onClick={() => openDrill({ willing: "Partial future" })} />
          </Box>

          {/* ── STACKED AREA CHART (click point → drill date) ── */}
          <SectionTitle>Willingness over time</SectionTitle>

          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <defs>
                  {WILLING_KEYS.map(key => (
                    <linearGradient key={key} id={`grad_${key.replace(/ /g,"_")}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={WILLING_COLORS[key]} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={WILLING_COLORS[key]} stopOpacity={0.4} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {["Not Willing","Partial future","Full future","Partial today","Full today"].map(key => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stackId="willing"
                    stroke={WILLING_COLORS[key]}
                    fill={`url(#grad_${key.replace(/ /g,"_")})`}
                    strokeWidth={1}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DISTRIBUTION BAR (click bar → drill bucket) ──── */}
          <SectionTitle>Overall distribution</SectionTitle>

          <ChartCard height={180}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distData} layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="label"
                  tick={{ fontSize: 11, fill: "#555" }} width={110} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${p.payload.pct.toFixed(2)}% (${p.payload.count.toLocaleString()})`,
                    "Count"
                  ]}
                />
                <Bar
                  dataKey="pct"
                  name="%"
                  radius={[0,4,4,0]}
                  cursor="pointer"
                >
                  {distData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color}
                      onClick={() => openDrill({ willing: entry.key })}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 1 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const total = safeNum(row["Total"]) || 1;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  alignItems: "center",
                }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openDrill({ date: row.ConvDate })}
                      sx={{ width: 24, height: 24 }}
                      title="View conversations for this date"
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box>{fmtDate(row.ConvDate)}</Box>
                  {CELL_COLS.map(({ key, color }) => {
                    const count = safeNum(row[key]);
                    const clickable = count > 0;
                    return (
                      <Box
                        key={key}
                        onClick={clickable
                          ? () => openDrill({ willing: key, date: row.ConvDate })
                          : undefined}
                        sx={{
                          textAlign: "right",
                          color: clickable ? color : "text.disabled",
                          fontWeight: clickable && key === "Full today" ? 500 : 400,
                          cursor: clickable ? "pointer" : "default",
                          "&:hover": clickable ? { textDecoration: "underline" } : {},
                        }}
                      >
                        {count}
                        <Box component="span" sx={{ fontSize: 10, color: "text.secondary", ml: 0.5 }}>
                          ({(count/total*100).toFixed(0)}%)
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ textAlign: "right", fontWeight: 500 }}>
                    {safeNum(row["Total"]).toLocaleString()}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default Willing;